<template>
  <button type="button" class="dsb" :class="{ 'dsb--light': light, 'is-saved': justSaved, 'is-busy': saving }"
    :disabled="saving" :title="tooltip" @click="$emit('save')">
    <span class="dsb__icon" aria-hidden="true">
      <svg v-if="saving" class="dsb__spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6"
        stroke-linecap="round">
        <path d="M12 3a9 9 0 1 0 9 9" />
      </svg>
      <svg v-else-if="justSaved" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8"
        stroke-linecap="round" stroke-linejoin="round">
        <path d="M20 6 9 17l-5-5" />
      </svg>
      <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
        stroke-linejoin="round">
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
        <path d="M17 21v-8H7v8M7 3v5h8" />
      </svg>
    </span>
    <span class="dsb__label" aria-live="polite">{{ label }}</span>
  </button>
</template>

<script>
export default {
  name: "DraftSaveButton",
  props: {
    saving: { type: Boolean, default: false },
    savedAt: { type: [String, Date], default: null },
    // true כשהכפתור יושב על רקע בהיר במקום על ההדר הכהה
    light: { type: Boolean, default: false },
  },
  data: () => ({
    justSaved: false,
    flashTimer: null,
  }),
  computed: {
    label() {
      if (this.saving) return "שומר";
      if (this.justSaved) return "נשמר";
      return "שמירת טיוטה";
    },
    tooltip() {
      if (!this.savedAt) return "שמירת הפרטים כטיוטה, כדי להמשיך אותם בהמשך";
      return `נשמר ${this.savedAgo}`;
    },
    savedAgo() {
      if (!this.savedAt) return "";

      const minutes = Math.floor((Date.now() - new Date(this.savedAt).getTime()) / 60000);
      if (minutes < 1) return "לפני רגע";
      if (minutes < 60) return `לפני ${minutes} דקות`;

      const hours = Math.floor(minutes / 60);
      return hours === 1 ? "לפני שעה" : `לפני ${hours} שעות`;
    },
  },
  watch: {
    savedAt(val) {
      if (!val) return;

      this.justSaved = true;
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => { this.justSaved = false; }, 2600);
    },
  },
  beforeDestroy() {
    clearTimeout(this.flashTimer);
  },
};
</script>

<style scoped>
.dsb {
  --dsb-fg: #ffffff;
  --dsb-bg: rgba(255, 255, 255, 0.10);
  --dsb-border: rgba(255, 255, 255, 0.24);
  --dsb-accent: #fdb721;

  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 14px 7px 12px;
  border: 1px solid var(--dsb-border);
  border-radius: 999px;
  background: var(--dsb-bg);
  color: var(--dsb-fg);
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s cubic-bezier(0.22, 1, 0.36, 1),
    border-color 0.2s cubic-bezier(0.22, 1, 0.36, 1),
    color 0.2s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.12s cubic-bezier(0.22, 1, 0.36, 1);
}

.dsb:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.18);
  border-color: rgba(255, 255, 255, 0.4);
}

.dsb:active:not(:disabled) {
  transform: translateY(1px);
}

.dsb:focus-visible {
  outline: 2px solid var(--dsb-accent);
  outline-offset: 2px;
}

.dsb:disabled {
  cursor: default;
}

.dsb.is-saved {
  color: var(--dsb-accent);
  border-color: rgba(253, 183, 33, 0.5);
  background: rgba(253, 183, 33, 0.14);
}

/* גרסה לרקע בהיר, אם תעביר את הכפתור לתוך הכרטיס הלבן */
.dsb--light {
  --dsb-fg: #0d2c6d;
  --dsb-bg: #ffffff;
  --dsb-border: #e3e8ef;
  box-shadow: 0 1px 2px rgba(13, 44, 109, 0.04), 0 1px 3px rgba(13, 44, 109, 0.06);
}

.dsb--light:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.dsb--light.is-saved {
  color: #16a34a;
  border-color: rgba(22, 163, 74, 0.35);
  background: #f0fdf4;
}

.dsb__icon {
  display: flex;
  width: 15px;
  height: 15px;
}

.dsb__icon svg {
  width: 100%;
  height: 100%;
}

.dsb__spinner {
  animation: dsbSpin 0.75s linear infinite;
}

@keyframes dsbSpin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {

  .dsb,
  .dsb__spinner {
    transition: none;
    animation-duration: 2s;
  }
}

/* במסכים צרים נשאר רק האייקון, כדי לא לדחוס את ההדר */
@media (max-width: 420px) {
  .dsb {
    padding: 7px 9px;
  }

  .dsb__label {
    display: none;
  }

  .dsb.is-saved .dsb__label {
    display: inline;
  }
}
</style>